import { useState, useEffect, useLayoutEffect, useMemo, useCallback, type ReactNode } from 'react';
import useSWR from 'swr';
import { createPortal } from 'react-dom';
import {
  IAddExternalProductsToLogisticServiceResponse,
  IEmployeeAvailableTool,
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  IProductReference,
  LOCATION_PLACE_TYPE
} from '../../../../../../../types/services';
import { getEmployeeTools } from '../../../../../../../services/toolsModule';
import ToolsSearchDropdown from './components/ToolsSearchDropdown/ToolsSearchDropdown';
import EmployeeOriginAvailableToolsList from './components/EmployeeOriginAvailableToolsList/EmployeeOriginAvailableToolsList';
import MultipleSerialToolCard from 'components/NewMultipleSerialToolCard/MultipleSerialToolCard';
import useDebounce from 'hooks/useDebounce';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import { LogisticsOriginAndDestinationInfo } from '../LogisticsOriginAndDestinationInfo/LogisticsOriginAndDestinationInfo';
import {
  buildAddExternalProductsDTO,
  isEmployeeAvailableToolForOffboardingOriginList
} from '../../../../../../../utils/logisticServiceOriginDestination.utils';
import { BordButton, BordFeedbackAlert, BordIlustrationIcon } from 'bord-design-system';
import { BordOneToneIcon, NudosGenericModal } from 'components/NudosComponents';
import BordAnimationLoader from 'components/BordAnimationLoader/BordAnimationLoader';
import {
  searchReferenceProducts,
  addExternalProductsToLogisticService,
  assignProductsToLogisticService,
  getLogisticServiceDetailsById
} from '../../../../../../../services/services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import { bordColorTheme } from '../../../../../../../styles/styles.constants';

import type { OffboardingLateralChrome } from '../../offboardingLateralModal.types';
import { SWR_KEYS } from 'constants/constants';

import './AddAdditionalToolsSection.scss';

type AddToolsSubmitFlags = {
  needsExternalProducts: boolean;
  needsAssignProducts: boolean;
};

interface IConfirmationModalProps {
  title: string;
  description: string;
  handleClose: () => void;
  handleConfirmation: () => void;
  isLoading: boolean;
}

const ConfirmationModal = ({
  title,
  description,
  handleClose,
  handleConfirmation,
  isLoading
}: IConfirmationModalProps) => {
  return createPortal(
    <NudosGenericModal
      modalContent={
        <div className="modalContainer" onClick={event => event.stopPropagation()}>
          <button
            type="button"
            className="modalCloseButton"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Cerrar"
          >
            <BordOneToneIcon variant="x" standardSize={18} stroke='#C6C7CB' />
          </button>
          <div className="modalIconContainer">
            <BordIlustrationIcon variant="warning" standardSize={72} />
          </div>
          <h2 className="modalTitle">{title}</h2>
          <p className="modalDescription">{description}</p>
          <div className="modalButtonsContainer">
            <BordButton
              type="button"
              label="No, cancelar"
              bordButtonStyle="outlined"
              modeButton="primary"
              onClick={handleClose}
              disabled={isLoading}
              customClassName="additionalToolsConfirmCancelBtn"
              customWidth='w-180'
            />
            <BordButton
              type="button"
              label="Sí, agregar herramientas"
              onClick={handleConfirmation}
              disabled={isLoading}
              isLoading={isLoading}
              customClassName="additionalToolsConfirmSubmitBtn"
              customWidth='w-180'
            />
          </div>
        </div>
      }
      closeModalCallback={handleClose}
      clickOutsideCallback={handleClose}
      customClassName="offboardingAdditionalToolsConfirmationOverlay"
    />,
    document.body
  );
};

interface ISelectedToolState {
  tool: IProductReference;
  quantity: number;
  serials: string[];
}

interface AddAdditionalToolsSectionProps {
  closeModalCallback: () => void;
  cardData: IlogisticServiceTrelloCardData;
  serviceDetails: IlogisticServiceDetails;
  isUpdateTools?: boolean;
  onLateralChromeChange?: (chrome: OffboardingLateralChrome | null) => void;
}

/**
 * Paso para agregar herramientas adicionales: cuerpo del panel lateral; cabecera (título, subtítulo e icono `mapPin`) y pie los define el padre vía callback.
 */
const AddAdditionalToolsSection = ({
  closeModalCallback,
  cardData,
  serviceDetails,
  isUpdateTools = false,
  onLateralChromeChange
}: AddAdditionalToolsSectionProps) => {
  const {
    setCurrentStep,
    setAvailableTools,
    setSelectedTools,
    setUnselectedTools,
    markedNotDeliveredToolIds,
    availableTools: serviceToolsInSelection
  } = useDeliveredToolsState();

  const [searchQuery, setSearchQuery] = useState('');
  const [availableTools, setAvailableToolsLocal] = useState<IProductReference[]>([]);
  const [selectedToolsState, setSelectedToolsState] = useState<ISelectedToolState[]>([]);
  const [serialErrors, setSerialErrors] = useState<Set<string>>(new Set());
  const [serialValidating, setSerialValidating] = useState<Set<string>>(new Set());
  const [duplicateSerialKeys, setDuplicateSerialKeys] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedEmployeeOriginAssignmentIds, setSelectedEmployeeOriginAssignmentIds] = useState<Set<string>>(
    () => new Set()
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  const assigneeUserId =
    serviceDetails?.originLocation?.userId ??
    null;

  const { data: employeeOriginTools = [] } = useSWR<IEmployeeAvailableTool[]>(
    [SWR_KEYS.employeeOriginTools, assigneeUserId],
    ([, userId]: readonly [string, number]) => getEmployeeTools(String(userId), 'false'),
    {
      revalidateOnFocus: false,
    }
  );

  const selectedEmployeeOriginProductIds = useMemo(() => {
    const ids = new Set<number>();
    employeeOriginTools?.forEach(tool => {
      if (!isEmployeeAvailableToolForOffboardingOriginList(tool)) return;
      if (!selectedEmployeeOriginAssignmentIds?.has(String(tool?.assignmentId))) return;
      ids.add(tool?.productId);
    });
    return Array.from(ids);
  }, [employeeOriginTools, selectedEmployeeOriginAssignmentIds]);

  /** Productos que ya están en el listado del servicio (paso de selección); no deben ofrecerse otra vez desde el empleado. */
  const productIdsAlreadyInService = useMemo(() => {
    const ids = new Set<number>();
    serviceToolsInSelection?.forEach(tool => {
      const pid = tool?.productId;
      if (pid != null && Number.isFinite(pid)) {
        ids.add(pid);
      }
    });
    return ids;
  }, [serviceToolsInSelection]);

  const employeeOriginDisplayName = useMemo(() => {
    const collectionUser = serviceDetails?.collectionInfo?.user;
    const fromPerson = [collectionUser?.firstName, collectionUser?.lastName].filter(Boolean).join(' ').trim();
    if (fromPerson) {
      return fromPerson;
    }
    const originTitle = cardData?.origin?.title?.trim();
    if (originTitle) {
      return originTitle;
    }
    return 'el empleado';
  }, [
    serviceDetails?.collectionInfo?.user?.firstName,
    serviceDetails?.collectionInfo?.user?.lastName,
    cardData?.origin?.title
  ]);

  const addToolsNewToolsRecipientPhrase = useMemo(() => {
    const originPlace = serviceDetails?.originLocation?.place;
    if (originPlace === LOCATION_PLACE_TYPE.OFFICE) {
      return 'a la oficina';
    }
    return 'al empleado';
  }, [serviceDetails?.originLocation?.place]);

  const selectedToolIds = new Set(selectedToolsState.map(item => item.tool.id));

  const toggleEmployeeOriginAssignment = useCallback((assignmentKey: string) => {
    setSelectedEmployeeOriginAssignmentIds(prev => {
      const next = new Set(prev);
      if (next?.has(assignmentKey)) next?.delete(assignmentKey);
      else next?.add(assignmentKey);
      return next;
    });
  }, []);

  const checkIfNextButtonShouldBeDisabled = (): boolean => {
    if (isSubmitting) return true;

    const hasDropdownSelection = selectedToolsState?.length > 0;
    const hasEmployeeOriginSelection = selectedEmployeeOriginAssignmentIds?.size > 0;
    if (!hasDropdownSelection && !hasEmployeeOriginSelection) return true;

    if (!hasDropdownSelection) return false;

    const hasToolsWithoutSerial = selectedToolsState?.some(toolState =>
      toolState?.serials?.some(serial => !serial || serial?.trim() === '')
    );
    if (hasToolsWithoutSerial) return true;

    if (serialErrors.size > 0) return true;
    if (serialValidating.size > 0) return true;
    if (duplicateSerialKeys.size > 0) return true;

    return false;
  };

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  const updateToolsAfterAddingExternalProducts = async (): Promise<void> => {
    if (!serviceDetails?.id) return;

    try {
      const updatedServiceDetails = await getLogisticServiceDetailsById(serviceDetails.id);

      if (updatedServiceDetails?.tools) {
        if (isUpdateTools) {
          setAvailableTools(updatedServiceDetails.tools);
          const selectedIndices: number[] = [];
          const unselectedIndices: number[] = [];

          updatedServiceDetails.tools.forEach((tool, index) => {
            if (tool.isMarkedNotDelivered === true) {
              unselectedIndices.push(index);
            } else {
              selectedIndices.push(index);
            }
          });

          setSelectedTools(selectedIndices);
          setUnselectedTools(unselectedIndices);
        } else {
          const onlyDeliverableTools = updatedServiceDetails.tools.filter(tool => {
            return !markedNotDeliveredToolIds.includes(tool?.productId as number);
          });
          setAvailableTools(onlyDeliverableTools);

          const allIndices = onlyDeliverableTools.map((_, index) => index);
          setSelectedTools([]);
          setUnselectedTools(allIndices);
        }
      }
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener los detalles actualizados del servicio</>
      });
    }
  };

  const transformSelectedToolsForDTO = (): { tools: IProductReference[]; serials: { [key: number]: string } } => {
    const tools: IProductReference[] = [];
    const serials: { [key: number]: string } = {};

    let globalIndex = 0;
    selectedToolsState?.forEach(toolState => {
      toolState?.serials?.forEach(serial => {
        tools.push(toolState.tool);
        serials[globalIndex] = serial;
        globalIndex++;
      });
    });

    return { tools, serials };
  };

  const buildAddToolsSuccessMessage = (
    flags: AddToolsSubmitFlags,
    createdRecordsFromExternal: number,
    assignedProductCount: number
  ): ReactNode => {
    if (flags.needsExternalProducts && flags.needsAssignProducts) {
      return `${createdRecordsFromExternal} herramientas nuevas agregadas y ${assignedProductCount} productos del empleado asignados al servicio`;
    }
    if (flags.needsExternalProducts) {
      return `${createdRecordsFromExternal} herramientas agregadas exitosamente`;
    }
    return 'Los productos seleccionados se asignaron al servicio correctamente';
  };


  const resetAddAdditionalToolsLocalState = (): void => {
    setSelectedToolsState([]);
    setSelectedEmployeeOriginAssignmentIds(new Set());
    setSearchQuery('');
    setSerialErrors(new Set());
    setSerialValidating(new Set());
    setDuplicateSerialKeys(new Set());
    setAvailableToolsLocal([]);
  };

  const handleConfirmAddTools = async (): Promise<void> => {
    setShowConfirmationModal(false);
    setIsSubmitting(true);

    const needsExternalProducts = selectedToolsState.length > 0;
    const needsAssignProducts = selectedEmployeeOriginProductIds.length > 0;

    if (!needsExternalProducts && !needsAssignProducts) {
      displayErrorNotification({
        customJSXMessage: <>Selecciona al menos una herramienta para continuar</>
      });
      setIsSubmitting(false);
      return;
    }

    if (!serviceDetails?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: datos del servicio incompletos</>
      });
      setIsSubmitting(false);
      return;
    }

    let dto: ReturnType<typeof buildAddExternalProductsDTO> = null;
    if (needsExternalProducts) {
      const { tools, serials } = transformSelectedToolsForDTO();
      dto = buildAddExternalProductsDTO(serviceDetails, tools, serials);
      if (!dto) {
        displayErrorNotification({
          customJSXMessage: <>Error: Faltan datos requeridos para agregar las herramientas</>
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const submitFlags: AddToolsSubmitFlags = {
        needsExternalProducts,
        needsAssignProducts
      };

      const [externalResponse] = await Promise.all([
        needsExternalProducts
          ? addExternalProductsToLogisticService(dto!)
          : Promise.resolve<IAddExternalProductsToLogisticServiceResponse | null>(null),
        needsAssignProducts
          ? assignProductsToLogisticService({
              logisticServiceId: serviceDetails.id,
              productIds: selectedEmployeeOriginProductIds
            })
          : Promise.resolve(null)
      ]);

      if (needsExternalProducts) {
        const createdRecordsFromExternal = externalResponse?.createdRecords ?? 0;
        if (createdRecordsFromExternal === 0) {
          displayErrorNotification({
            customJSXMessage: <>Error al agregar herramientas</>
          });
          setIsSubmitting(false);
          return;
        }
      }

      await updateToolsAfterAddingExternalProducts();

      const createdRecordsFromExternal = externalResponse?.createdRecords ?? 0;
      displaySuccessNotification({
        customJSXMessage: (
          <>
            {buildAddToolsSuccessMessage(
              submitFlags,
              createdRecordsFromExternal,
              selectedEmployeeOriginProductIds.length
            )}
          </>
        )
      });
      setIsSubmitting(false);

      resetAddAdditionalToolsLocalState();
      setCurrentStep(SECTIONS_STEPS.SELECTION);
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al agregar herramientas adicionales</>
      });
      setIsSubmitting(false);
    }
  };

  const onBack = () => {
    setCurrentStep(SECTIONS_STEPS.SELECTION);
  };

  const getContent = (isUpdateTools: boolean) => {
    if (isUpdateTools) {
      return {
        additionalToolsTitle: `Agrega las herramientas que llegarán adicionales`,
        headerSubtitle: `Busca y selecciona las herramientas que llegarán adicionales`,
        dropdownTitle: `Busca y selecciona las herramientas que llegaron adicionales`,
        nextButton: {
          text: 'Agregar herramientas',
          action: () => handleShowConfirmationModal(),
          disabledCondition: () => checkIfNextButtonShouldBeDisabled(),
          isLoading: isSubmitting
        },
        backButton: {
          text: 'Volver',
          action: () => onBack()
        }
      };
    }

    return {
      additionalToolsTitle: `Pasar a entregado`,
      headerSubtitle: `Selecciona las herramientas entregadas y confirma la fecha`,
      dropdownTitle: `Busca y selecciona las herramientas que llegaron adicionalmente`,
      nextButton: {
        text: 'Agregar herramientas',
        action: () => handleShowConfirmationModal(),
        disabledCondition: () => checkIfNextButtonShouldBeDisabled(),
        isLoading: isSubmitting
      },
      backButton: {
        text: 'Volver',
        action: () => onBack()
      }
    };
  };

  const content = getContent(isUpdateTools);

  const handleToolToggle = (tool: IProductReference) => {
    setSelectedToolsState(prev => {
      const existingIndex = prev.findIndex(item => item.tool.id === tool.id);

      if (existingIndex >= 0) {
        const newState = [...prev];
        newState.splice(existingIndex, 1);
        return newState;
      }

      return [...prev, { tool, quantity: 1, serials: [''] }];
    });
  };

  const handleQuantityChange = (toolIndex: number, quantity: number) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      const currentSerials = newState[toolIndex].serials;

      const newSerials = [...currentSerials];
      while (newSerials.length < quantity) newSerials.push('');
      while (newSerials.length > quantity) newSerials.pop();

      newState[toolIndex] = { ...newState[toolIndex], quantity, serials: newSerials };
      return newState;
    });
  };

  const handleSerialsChange = (toolIndex: number, serials: string[]) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      newState[toolIndex] = { ...newState[toolIndex], serials };
      return newState;
    });
  };

  const addOrRemoveKey = (prevSet: Set<string>, key: string, shouldAdd: boolean): Set<string> => {
    const newSet = new Set(prevSet);
    if (shouldAdd) newSet.add(key);
    else newSet.delete(key);
    return newSet;
  };

  const handleSerialErrorChange = (toolIndex: number, serialIndex: number, hasError: boolean) => {
    const key = `${toolIndex}-${serialIndex}`;
    setSerialErrors(prev => addOrRemoveKey(prev, key, hasError));
  };

  const handleSerialValidatingChange = (toolIndex: number, serialIndex: number, isValidating: boolean) => {
    const key = `${toolIndex}-${serialIndex}`;
    setSerialValidating(prev => addOrRemoveKey(prev, key, isValidating));
  };

  const adjustIndicesAfterRemoval = (prevSet: Set<string>, removedIndex: number): Set<string> => {
    const newSet = new Set<string>();
    prevSet.forEach(key => {
      const [idx, serialIdx] = key.split('-').map(Number);
      if (idx < removedIndex) newSet.add(key);
      else if (idx > removedIndex) newSet.add(`${idx - 1}-${serialIdx}`);
    });
    return newSet;
  };

  const removeTool = (toolIndex: number) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      newState.splice(toolIndex, 1);
      return newState;
    });

    setSerialErrors(prev => adjustIndicesAfterRemoval(prev, toolIndex));
    setSerialValidating(prev => adjustIndicesAfterRemoval(prev, toolIndex));
  };

  const findDuplicateSerialKeys = (): Set<string> => {
    const serialOccurrences = new Map<string, string[]>();
    const duplicates = new Set<string>();

    selectedToolsState.forEach((toolState, toolIndex) => {
      toolState.serials.forEach((serial, serialIndex) => {
        if (!serial || serial.trim() === '') return;

        const normalizedSerial = serial.trim().toLowerCase();
        const key = `${toolIndex}-${serialIndex}`;

        if (!serialOccurrences.has(normalizedSerial)) {
          serialOccurrences.set(normalizedSerial, []);
        }
        serialOccurrences.get(normalizedSerial)!.push(key);
      });
    });

    serialOccurrences.forEach(keys => {
      if (keys.length > 1) {
        keys.forEach(key => duplicates.add(key));
      }
    });

    return duplicates;
  };

  const getDuplicateIndicesForTool = (toolIndex: number): Set<number> => {
    const indices = new Set<number>();
    duplicateSerialKeys.forEach(key => {
      const [toolIdx, serialIdx] = key.split('-').map(Number);
      if (toolIdx === toolIndex) indices.add(serialIdx);
    });
    return indices;
  };

  const fetchTools = async (query: string) => {
    try {
      const response = await searchReferenceProducts({ query });
      setAvailableToolsLocal(response);
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error buscando herramientas</>
      });
    }
  };

  const handleSearchTools = (query: string) => {
    if (!query || query.trim() === '') return;
    fetchTools(query);
  };

  useEffect(() => {
    const duplicates = findDuplicateSerialKeys();
    setDuplicateSerialKeys(duplicates);
  }, [selectedToolsState]);

  useEffect(() => {
    return handleSearchTools(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // getContent y condiciones del pie se evalúan dentro del efecto; evitamos dependencias inestables por funciones.
  useLayoutEffect(() => {
    if (!onLateralChromeChange) {
      return undefined;
    }

    const chromeContent = getContent(isUpdateTools);
    const isFooterPrimaryDisabled = chromeContent.nextButton.disabledCondition();

    onLateralChromeChange({
      headerProps: {
        title: chromeContent.additionalToolsTitle,
        subtitle: chromeContent.headerSubtitle,
        subtitleClassName: 'text-xss text-nudos-gray-500 font-normal',
        leading: (
          <span className="addAdditionalToolsSectionHeaderLeading">
            <BordOneToneIcon variant="mapPin" customWidth="2.4rem" stroke={bordColorTheme.stroke.default} />
          </span>
        ),
        showCloseButton: true,
        onClose: closeModalCallback
      },
      footerProps: {
        firstButton: {
          label: chromeContent.backButton.text,
          modeButton: 'secondary',
          customWidth: 'w-full',
          onClick: chromeContent.backButton.action
        },
        secondButton: {
          label: chromeContent.nextButton.text,
          modeButton: 'primary',
          customWidth: 'w-full',
          onClick: chromeContent.nextButton.action,
          disabled: isFooterPrimaryDisabled,
          isLoading: chromeContent.nextButton.isLoading
        }
      }
    });

    return undefined;
  }, [
    onLateralChromeChange,
    closeModalCallback,
    isUpdateTools,
    isSubmitting,
    selectedToolsState,
    serialErrors,
    serialValidating,
    duplicateSerialKeys,
    selectedEmployeeOriginAssignmentIds
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="addAdditionalToolsSectionContainer">
      <LogisticsOriginAndDestinationInfo cardData={cardData} />

      <div className="contentContainer">
        <div className="dropdownContainer">
          <h1 className="dropdownTitle">{content.dropdownTitle}</h1>
          <div className="dropdownContent">
            <ToolsSearchDropdown
              searchQuery={searchQuery}
              availableTools={availableTools}
              selectedToolIds={selectedToolIds}
              onSearchQueryChange={setSearchQuery}
              onToolToggle={handleToolToggle}
            />
          </div>
        </div>

        {selectedToolsState?.length > 0 && (
          <>
            <div className="toolsDivider" />
            <BordFeedbackAlert
              type="warning"
              className="customBordFeedbackAlert"
              title={
                <div className="alertContent">
                  {`Se agregarán herramientas nuevas ${addToolsNewToolsRecipientPhrase} (sin reemplazar las del servicio) y luego se moverán a Bodegas. Esta acción es irreversible y podría generar costos adicionales.`}
                </div>
              }
            />
          </>
        )}

        <div className="toolsContainer">
          {selectedToolsState?.length > 0 && (
            <>
              {selectedToolsState.map((toolState, index) => (
                <MultipleSerialToolCard
                  key={`selected-${toolState.tool.id}-${index}`}
                  tool={toolState.tool}
                  quantity={toolState.quantity}
                  serials={toolState.serials}
                  duplicateSerialIndices={getDuplicateIndicesForTool(index)}
                  onQuantityChange={quantity => handleQuantityChange(index, quantity)}
                  onSerialsChange={serials => handleSerialsChange(index, serials)}
                  onSerialErrorChange={(serialIndex, hasError) => handleSerialErrorChange(index, serialIndex, hasError)}
                  onSerialValidatingChange={(serialIndex, isValidating) =>
                    handleSerialValidatingChange(index, serialIndex, isValidating)
                  }
                  onRemove={() => removeTool(index)}
                />
              ))}
            </>
          )}
          <div className='mt-8' />
          <EmployeeOriginAvailableToolsList
            employeeDisplayName={employeeOriginDisplayName}
            tools={employeeOriginTools}
            productIdsAlreadyInService={productIdsAlreadyInService}
            selectedAssignmentIds={selectedEmployeeOriginAssignmentIds}
            onToggleAssignment={toggleEmployeeOriginAssignment}
          />
        </div>
      </div>

      {isSubmitting &&
        createPortal(
          <div className="addAdditionalToolsFullViewportLoader" aria-busy="true" aria-live="polite">
            <BordAnimationLoader lottieCustomClassName="h-96" />
          </div>,
          document.body
        )}

      {showConfirmationModal && (
        <ConfirmationModal
          title="¿Agregar herramientas no solicitadas?"
          description="Estás a punto de agregar herramientas que no fueron solicitadas en este servicio. Serán visibles para el cliente y pueden generar costos adicionales."
          handleConfirmation={handleConfirmAddTools}
          handleClose={handleCancelConfirmation}
          isLoading={isSubmitting}
        />
      )}
    </div>
  );
};

export default AddAdditionalToolsSection;
